<script setup lang="ts">
import { ref } from 'vue';
import { RouterLink, useRoute } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const authStore = useAuthStore();
const route = useRoute();
const isMenuOpen = ref(false);

const navigationItems = [
  { path: '/', label: 'Home' },
  { path: '/produkte', label: 'Produkte' },
  { path: '/unternehmen', label: 'Unternehmen' },
  { path: '/karriere', label: 'Karriere' },
  { path: '/aktuelles', label: 'Aktuelles' },
  { path: '/kontakt', label: 'Kontakt' }
];

const isActiveRoute = (path: string) => {
  if (path === '/') return route.path === '/';
  return route.path.startsWith(path);
};

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  if (isMenuOpen.value) {
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
};

const closeMenu = () => {
  isMenuOpen.value = false;
  document.body.style.overflow = '';
};

const handleKeydown = (e: KeyboardEvent) => {
  if (e.key === 'Escape' && isMenuOpen.value) {
    closeMenu();
  }
};
</script>

<template>
  <header class="header" @keydown="handleKeydown">
    <div class="header-inner container">
      <!-- Logo -->
      <RouterLink to="/" class="logo" @click="closeMenu">
        <img src="/mr-logo.svg" alt="M&R Filtermedien" class="logo-img" width="287" height="287" />
        <span class="logo-label">M&R Filtermedien GmbH</span>
      </RouterLink>

      <!-- Desktop Navigation -->
      <nav class="nav-desktop" aria-label="Hauptnavigation">
        <ul class="nav-list">
          <li v-for="item in navigationItems" :key="item.path">
            <RouterLink
              :to="item.path"
              class="nav-link"
              :class="{ active: isActiveRoute(item.path) }"
              :aria-current="isActiveRoute(item.path) ? 'page' : undefined"
            >
              {{ item.label }}
            </RouterLink>
          </li>
        </ul>

        <RouterLink
          v-if="authStore.isAuthenticated"
          to="/admin"
          class="btn btn-sm btn-secondary"
        >
          Admin
        </RouterLink>
      </nav>

      <!-- Mobile Menu Toggle -->
      <button
        class="menu-toggle"
        :class="{ open: isMenuOpen }"
        @click="toggleMenu"
        :aria-expanded="isMenuOpen"
        :aria-label="isMenuOpen ? 'Menü schließen' : 'Menü öffnen'"
        aria-controls="mobile-nav"
      >
        <span class="menu-bar"></span>
        <span class="menu-bar"></span>
      </button>
    </div>

    <!-- Mobile Navigation -->
    <nav
      id="mobile-nav"
      class="nav-mobile"
      :class="{ open: isMenuOpen }"
      aria-label="Mobile Navigation"
    >
      <ul class="nav-mobile-list">
        <li v-for="item in navigationItems" :key="item.path">
          <RouterLink
            :to="item.path"
            class="nav-mobile-link"
            :class="{ active: isActiveRoute(item.path) }"
            :aria-current="isActiveRoute(item.path) ? 'page' : undefined"
            @click="closeMenu"
          >
            {{ item.label }}
          </RouterLink>
        </li>
        <li v-if="authStore.isAuthenticated">
          <RouterLink
            to="/admin"
            class="nav-mobile-link"
            @click="closeMenu"
          >
            Admin Dashboard
          </RouterLink>
        </li>
      </ul>
    </nav>
  </header>

  <!-- Overlay -->
  <div
    v-if="isMenuOpen"
    class="menu-overlay"
    @click="closeMenu"
    role="presentation"
  ></div>
</template>

<style scoped>
.header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-fixed);
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--border-color);
}

.header-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: var(--nav-height);
}

/* Logo */
.logo {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  text-decoration: none;
  line-height: 1;
}

.logo-img {
  width: 36px;
  height: 36px;
  flex-shrink: 0;
}

.logo-label {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-tertiary);
}

/* Desktop Navigation */
.nav-desktop {
  display: flex;
  align-items: center;
  gap: var(--spacing-8);
}

.nav-list {
  display: flex;
  gap: var(--spacing-1);
}

.nav-link {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
  text-decoration: none;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: var(--radius-md);
  transition: color var(--transition-fast), background var(--transition-fast);
}

.nav-link:hover {
  color: var(--text-primary);
  background: var(--bg-secondary);
}

.nav-link.active {
  color: var(--text-primary);
  font-weight: var(--font-weight-semibold);
}

/* Mobile Menu Toggle — minimal 2-bar design */
.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  width: 44px;
  height: 44px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 10px;
}

.menu-bar {
  display: block;
  width: 100%;
  height: 2px;
  background: var(--text-primary);
  border-radius: 1px;
  transition: all var(--transition-fast);
  transform-origin: center;
}

.menu-toggle.open .menu-bar:first-child {
  transform: rotate(45deg) translate(3px, 3px);
}

.menu-toggle.open .menu-bar:last-child {
  transform: rotate(-45deg) translate(3px, -3px);
}

/* Mobile Navigation */
.nav-mobile {
  display: none;
  position: fixed;
  top: var(--nav-height);
  right: -100%;
  width: min(80%, 360px);
  height: calc(100vh - var(--nav-height));
  background: var(--bg-primary);
  border-left: 1px solid var(--border-color);
  transition: right var(--transition-normal);
  overflow-y: auto;
}

.nav-mobile.open {
  right: 0;
}

.nav-mobile-list {
  padding: var(--spacing-6);
}

.nav-mobile-link {
  display: block;
  padding: var(--spacing-4);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-medium);
  color: var(--text-primary);
  text-decoration: none;
  border-radius: var(--radius-lg);
  transition: all var(--transition-fast);
}

.nav-mobile-link:hover,
.nav-mobile-link.active {
  background: var(--bg-secondary);
  color: var(--color-primary);
}

/* Menu Overlay */
.menu-overlay {
  display: none;
  position: fixed;
  top: var(--nav-height);
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: calc(var(--z-fixed) - 1);
}

/* Responsive */
@media (max-width: 900px) {
  .logo-label { display: none; }
}

@media (max-width: 768px) {
  .nav-desktop {
    display: none;
  }

  .menu-toggle,
  .nav-mobile {
    display: flex;
  }

  .nav-mobile {
    flex-direction: column;
  }

  .menu-overlay {
    display: block;
  }
}
</style>
